// Performance Type Color Mapping System

import { PerformanceTypeColor, CustomColorScheme } from '../types/colorTypes';
import { loadCustomColors } from './colorStorage';

// Predefined color palette for performance types
const COLOR_PALETTE: PerformanceTypeColor[] = [
  {
    backgroundColor: '#e3f2fd',
    borderColor: '#1976d2',
    textColor: '#0d47a1',
    name: 'Blue'
  },
  {
    backgroundColor: '#f3e5f5',
    borderColor: '#7b1fa2',
    textColor: '#4a148c',
    name: 'Purple'
  },
  {
    backgroundColor: '#e8f5e8',
    borderColor: '#388e3c',
    textColor: '#1b5e20',
    name: 'Green'
  },
  {
    backgroundColor: '#fff3e0',
    borderColor: '#f57c00',
    textColor: '#e65100',
    name: 'Orange'
  },
  {
    backgroundColor: '#fce4ec',
    borderColor: '#c2185b',
    textColor: '#880e4f',
    name: 'Pink'
  },
  {
    backgroundColor: '#e0f2f1',
    borderColor: '#00695c',
    textColor: '#004d40',
    name: 'Teal'
  },
  {
    backgroundColor: '#f1f8e9',
    borderColor: '#689f38',
    textColor: '#33691e',
    name: 'Light Green'
  },
  {
    backgroundColor: '#fff8e1',
    borderColor: '#ffa000',
    textColor: '#ff6f00',
    name: 'Amber'
  },
  {
    backgroundColor: '#f3e5f5',
    borderColor: '#512da8',
    textColor: '#311b92',
    name: 'Deep Purple'
  },
  {
    backgroundColor: '#e8eaf6',
    borderColor: '#303f9f',
    textColor: '#1a237e',
    name: 'Indigo'
  }
];

// Default color for unknown performance types
const DEFAULT_COLOR: PerformanceTypeColor = {
  backgroundColor: '#f5f5f5',
  borderColor: '#9e9e9e',
  textColor: '#424242',
  name: 'Gray'
};

// Color mapping cache
const performanceTypeColorMap = new Map<string, PerformanceTypeColor>();
let customColorsCache: CustomColorScheme | null = null;

/**
 * Get custom colors from storage (cached)
 */
const getCustomColors = (): CustomColorScheme => {
  if (customColorsCache === null) {
    customColorsCache = loadCustomColors();
  }
  return customColorsCache;
};

/**
 * Clear custom colors cache (call when colors are updated)
 */
export const clearCustomColorsCache = (): void => {
  customColorsCache = null;
  performanceTypeColorMap.clear();
};

/**
 * Get color for a performance type
 * @param performanceType The performance type string
 * @returns Color configuration object
 */
export const getPerformanceTypeColor = (performanceType: string | undefined): PerformanceTypeColor => {
  if (!performanceType) {
    return DEFAULT_COLOR;
  }

  // Check if we already have a color assigned
  if (performanceTypeColorMap.has(performanceType)) {
    return performanceTypeColorMap.get(performanceType)!;
  }

  // Check for custom color first
  const customColors = getCustomColors();
  if (customColors[performanceType]) {
    const customColor = customColors[performanceType];
    performanceTypeColorMap.set(performanceType, customColor);
    return customColor;
  }

  // Assign a new color based on the hash of the performance type
  const hash = getStringHash(performanceType);
  const colorIndex = hash % COLOR_PALETTE.length;
  const color = COLOR_PALETTE[colorIndex];

  // Cache the color assignment
  performanceTypeColorMap.set(performanceType, color);
  
  return color;
};

/**
 * Get all assigned colors for the current performance types
 * @param performanceTypes Array of performance types
 * @returns Map of performance type to color
 */
export const getPerformanceTypeColorMap = (performanceTypes: string[]): Map<string, PerformanceTypeColor> => {
  const colorMap = new Map<string, PerformanceTypeColor>();
  
  performanceTypes.forEach(type => {
    colorMap.set(type, getPerformanceTypeColor(type));
  });
  
  return colorMap;
};

/**
 * Simple string hash function for consistent color assignment
 * @param str String to hash
 * @returns Hash number
 */
function getStringHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Generate CSS custom properties for a performance type
 * @param performanceType The performance type
 * @returns CSS custom properties object
 */
export const getPerformanceTypeCSS = (performanceType: string | undefined): React.CSSProperties => {
  const color = getPerformanceTypeColor(performanceType);
  
  return {
    '--performance-bg': color.backgroundColor,
    '--performance-border': color.borderColor,
    '--performance-text': color.textColor,
    backgroundColor: color.backgroundColor,
    borderColor: color.borderColor,
    color: color.textColor,
    borderWidth: '2px',
    borderStyle: 'solid'
  } as React.CSSProperties;
};